import AttributeField from '@/Components/GIS/AttributeField'
import {
  getLayerFeature,
  getOperationalLayerById,
  updateLayerFeature,
} from '@/api/endpoints'
import { useSafeTheme } from '@/theme/hooks'
import { orderAttributeKeys } from '@/utils/attributeOrder'
import { proprietesModifiees, typeDeValeur } from '@/utils/attributeTypes'
import PropTypes from 'prop-types'
import { useEffect, useMemo, useRef, useState } from 'react'
import { estAttributSysteme, formaterCle } from './featurePopupFormat'

const AUCUNE_MODIF = { proprietes: {}, erreurs: {} }
const MESSAGES = {
  'lecture-seule': 'Couche en lecture seule : trop volumineuse pour l’édition',
  'echec-chargement': 'Impossible de charger l’entité',
}

const FeaturePopupEditor = ({ layerId, featureId, onSaved, onCancel }) => {
  const { theme } = useSafeTheme()
  const [etat, setEtat] = useState('chargement')
  const [donnees, setDonnees] = useState(null)
  const [saisies, setSaisies] = useState({})
  const [envoi, setEnvoi] = useState(false)
  const [echec, setEchec] = useState(false)
  const envoiRef = useRef(false)
  const afficheRef = useRef(true)

  useEffect(() => {
    afficheRef.current = true
    return () => {
      afficheRef.current = false
    }
  }, [])

  useEffect(() => {
    let annule = false
    Promise.all([
      getOperationalLayerById(layerId),
      getLayerFeature(layerId, featureId),
    ])
      .then(([couche, entite]) => {
        if (annule) return
        if (couche?.editable === false) {
          setEtat('lecture-seule')
          return
        }
        setDonnees({
          schema: couche?.properties_schema || {},
          ordre: couche?.properties_order,
          proprietes: entite?.properties || {},
        })
        setEtat('edition')
      })
      .catch(() => {
        if (!annule) setEtat('echec-chargement')
      })
    return () => {
      annule = true
    }
  }, [layerId, featureId])

  const colonnes = useMemo(() => {
    if (!donnees) return []
    const cles = new Set([
      ...Object.keys(donnees.schema),
      ...Object.keys(donnees.proprietes),
    ])
    return orderAttributeKeys(
      [...cles].filter(cle => !estAttributSysteme(cle)),
      donnees.ordre,
    )
  }, [donnees])

  const typeDe = cle =>
    typeDeValeur(donnees.schema, cle, [donnees.proprietes[cle]])
  const { proprietes: modifs, erreurs } = donnees
    ? proprietesModifiees(donnees.proprietes, saisies, typeDe)
    : AUCUNE_MODIF
  const peutEnregistrer =
    !envoi &&
    Object.keys(modifs).length > 0 &&
    Object.keys(erreurs).length === 0

  const enregistrer = async e => {
    e.preventDefault()
    if (!peutEnregistrer || envoiRef.current) return
    envoiRef.current = true
    setEnvoi(true)
    setEchec(false)
    try {
      const reponse = await updateLayerFeature(layerId, featureId, {
        properties: modifs,
      })
      // Fermee pendant l'envoi : la fiche a pu passer a une autre edition, qu'il ne faut pas clore.
      onSaved(
        {
          proprietes: { ...donnees.proprietes, ...modifs },
          updatedAt: reponse?.updated_at ?? null,
        },
        afficheRef.current,
      )
    } catch {
      envoiRef.current = false
      setEnvoi(false)
      setEchec(true)
    }
  }

  if (etat === 'chargement') {
    return (
      <p
        className="px-3 py-3 text-[11px]"
        style={{ color: theme.text.tertiary }}
      >
        Chargement…
      </p>
    )
  }

  if (etat !== 'edition') {
    return (
      <div className="flex items-center justify-between gap-3 px-3 py-3">
        <p className="text-[11px]" style={{ color: theme.text.tertiary }}>
          {MESSAGES[etat]}
        </p>
        <button
          type="button"
          onClick={onCancel}
          className="shrink-0 text-xs font-semibold text-[#0a84ff]"
        >
          Retour
        </button>
      </div>
    )
  }

  return (
    <form
      onSubmit={enregistrer}
      onKeyDown={e => {
        e.stopPropagation()
        if (e.key === 'Escape') onCancel()
      }}
      className="p-3"
    >
      <div className="max-h-72 space-y-2 overflow-y-auto">
        {colonnes.map(cle => {
          const id = `feature-popup-${cle}`
          const saisie = Object.prototype.hasOwnProperty.call(saisies, cle)
          return (
            <div key={cle}>
              <label
                htmlFor={id}
                className="mb-0.5 block text-[11px]"
                style={{ color: theme.text.tertiary }}
              >
                {formaterCle(cle)}
              </label>
              <AttributeField
                id={id}
                label={cle}
                type={typeDe(cle)}
                value={saisie ? saisies[cle] : donnees.proprietes[cle]}
                erreur={erreurs[cle]}
                onChange={valeur =>
                  setSaisies(prev => ({ ...prev, [cle]: valeur }))
                }
              />
            </div>
          )
        })}
      </div>
      {echec && (
        <p role="alert" className="mt-2 text-xs text-[#ff3b30]">
          Échec de l’enregistrement
        </p>
      )}
      <div className="mt-3 flex justify-end gap-4">
        <button
          type="button"
          onClick={onCancel}
          className="text-[13px] font-medium"
          style={{ color: theme.text.secondary }}
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={!peutEnregistrer}
          className="text-[13px] font-semibold text-[#0a84ff] disabled:opacity-40"
        >
          Enregistrer
        </button>
      </div>
    </form>
  )
}

FeaturePopupEditor.propTypes = {
  layerId: PropTypes.number.isRequired,
  featureId: PropTypes.string.isRequired,
  onSaved: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
}

export default FeaturePopupEditor
